<script setup lang="ts">
import type { RadioGroupItemProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { useForwardProps } from 'reka-ui';
import SphLabel from '@/components/base/label/';
import generateId from '@/utils/generateId';
import { cn } from '@/utils/shadcn/lib/utils.ts';
import RadioGroupItem from './RadioGroupItem.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: true,
});

const props = defineProps<RadioGroupItemProps & {
    class?: HTMLAttributes['class']
    value: any
    label: string
    description?: string
    checked?: boolean
}>();

const itemId = generateId('radio-group');

const delegatedProps = reactiveOmit(props, 'class');

const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <SphLabel
        :for="itemId"
        data-slot="radio-group-item-card"
        :data-checked="props.checked ? 'true' : undefined"
        class="sph-form-control"
        :class="cn(
            `relative bg-card p-5 border border-input-dark rounded-md cursor-pointer
            transition-colors duration-200
            hover:border-ring/50 hover:bg-primary/5 focus-within:border-ring/50
            data-checked:border-accent-light data-checked:bg-accent-light/25
            group-data-disabled/radio-group:pointer-events-none
            group-data-disabled/radio-group:opacity-50`, props.class)"
    >
        <RadioGroupItem
            v-bind="forwardedProps"
            :id="itemId"
            :value="props.value"
            class="absolute top-4 right-4"
        />

        <div class="flex flex-col gap-2">
            <SphLabel class="pointer-events-none">
                {{ props.label }}
            </SphLabel>
            <p
                class="description text-sm text-muted-foreground"
            >
                {{ props.description }}
            </p>
        </div>
    </SphLabel>
</template>
